// "Who sees which data" — a map anyone can read, in four steps:
//   person → assistant (Bro EFD / Bro PPI) → whose data (the data set, by
//   client name) → the database it is stored in.
// Lines between them: orange = sees everything, green = only a part, grey =
// no access / inactive, dashed = works on another server. Clicking anything
// lights up its connections and explains them in plain sentences on the
// right; technical details (host, database name) only appear there.
// Zoom with the wheel (towards the pointer) or the buttons; the map never
// pans, so nobody loses it — zooming out always shows everything again.
// The rules behind it live in ./access.js (same as the user page). People
// handed over to another instance get their path from that instance's
// access overview (names only, no client data). The servers cannot reach
// each other, the browser reaches both: it gets a short-lived ticket from
// this instance (/api/admin/instances/<key>/overview-ticket) and reads the
// overview there itself. Without it they show the plain hand-over line.

import { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark, faDatabase, faBuildingColumns, faChartPie, faServer, faMagnifyingGlassPlus, faMagnifyingGlassMinus,
  faExpand, faUserPen, faTriangleExclamation, faDiagramProject, faArrowLeft, faFolderOpen, faBan,
} from '@fortawesome/free-solid-svg-icons';
import { Avatar, Spinner, realName, naturalCompare } from './ui';
import { useConsole } from './context';
import { accessModel, assistantLabel, modelLabel, scopeWhat, variantLabel } from './access';
import './datamap.css';

// ── Geometry (canvas pixels at 100 %) ──
const SIZE = { person: [220, 48], assistant: [262, 72], set: [290, 76], db: [262, 72] };
const GAP_X = 80;
const ROW = 58;
const PAD = 28;
const TOP = 92;
const LABEL_H = 30;
const GROUP_GAP = 22;
const COL_X = { person: PAD };
COL_X.assistant = COL_X.person + SIZE.person[0] + GAP_X;
COL_X.set = COL_X.assistant + SIZE.assistant[0] + GAP_X;
COL_X.db = COL_X.set + SIZE.set[0] + GAP_X;
const WIDTH = COL_X.db + SIZE.db[0] + PAD;
const ASSISTANT_ORDER = ['efd', 'ppi'];
const COLUMNS = [
  { x: COL_X.person, w: SIZE.person[0], n: 1, title: 'People', sub: 'who log in here' },
  { x: COL_X.assistant, w: SIZE.assistant[0], n: 2, title: 'Assistant', sub: 'what they work with' },
  { x: COL_X.set, w: SIZE.set[0], n: 3, title: 'Whose data', sub: 'which client’s data' },
  { x: COL_X.db, w: SIZE.db[0], n: 4, title: 'Database', sub: 'where it is stored' },
];

const avg = (list) => list.reduce((a, b) => a + b, 0) / Math.max(list.length, 1);
const people = (n) => (n === 1 ? '1 person' : `${n} people`);
/** "advisor Bodmer, Sinan" — or "one consolidation" when only the kind is
 *  known (another instance never sends filter values). */
const partText = (scope) => (scope.value ? scopeWhat(scope) : `one ${(scope.label || 'part').toLowerCase()}`);
const scopeText = (p) => {
  if (p.noAccess) return 'No access';
  if (p.inactive) return 'Inactive';
  return p.scope.limited ? `Only ${partText(p.scope)}` : 'Everything';
};

/** Why a handed-over person shows no path (overview of their instance). */
function overviewNote(ov) {
  if (!ov) return 'Asking that server who uses what there …';
  if (ov.available) return 'That server reports no assistant with client data for them.';
  if (ov.reason === 'token_rejected') return 'That server rejected the access overview token — create a new one there and enter it under Instances.';
  if (ov.reason === 'token_unreadable') return 'The stored access overview token cannot be read any more — enter it again under Instances.';
  if (ov.reason === 'unreachable' || ov.reason === 'error') return 'That server could not be reached just now.';
  return 'That server does not share its access overview yet: create a token there (Instances → Access overview) and enter it here under Instances.';
}

/** Another instance's access overview, read by the browser with a ticket from this one. */
async function readOverview(client, key) {
  const t = await client.get(`/api/admin/instances/${encodeURIComponent(key)}/overview-ticket`);
  if (!t.available) return t;
  let r;
  try {
    r = await fetch(t.url, { headers: { [t.header]: t.ticket }, cache: 'no-store' });
  } catch (e) {
    return { available: false, reason: 'unreachable', error: e.message };
  }
  if (r.status === 401) return { available: false, reason: 'token_rejected' };
  const data = r.ok ? await r.json().catch(() => null) : null;
  if (!data || !Array.isArray(data.users)) return { available: false, reason: 'unreachable', error: `HTTP ${r.status}` };
  return { available: true, ...data };
}

/** Routed users with their path on the other instance (from its overview). */
function withRemote(model, remote) {
  const paths = [...model.paths];
  const routed = [];
  model.routed.forEach((r) => {
    const ov = remote[r.instance.key];
    const ru = ov?.available ? (ov.users || []).find((u) => u.username === r.user.username) : null;
    if (!ru || !(ru.assistants || []).length) {
      routed.push({ ...r, overview: ov, remoteUser: ru || null });
      return;
    }
    const place = ov.instance || r.instance.label;
    const k = r.instance.key;
    ru.assistants.forEach((e) => {
      const ds = e.data_set;
      paths.push({
        user: r.user, assistant: e.assistant, origin: e.origin, variant: e.variant, noAccess: !!e.no_access,
        inactive: r.inactive || ru.status === 'inactive',
        set: ds ? {
          key: `${k}:${ds.key}`, name: ds.name, model: ds.model, variant: e.variant,
          schema_map: Object.fromEntries((ds.schemas || []).map((x) => [x, x])),
        } : null,
        db: e.no_access ? null : (e.db
          ? { id: `${k}:${e.db.id}`, model: e.db.model, detail: e.db.detail, place }
          : { id: `${k}:env:${e.assistant}`, model: e.assistant, env: true, detail: 'server configuration', place }),
        scope: { limited: !!e.limited, label: e.kind || 'part', value: null },
        via: e.via || [], place, remote: r.instance,
      });
    });
  });
  return { ...model, paths, routed };
}
const setId = (p) => (p.set ? `s:${p.set.key}` : `s:env:${p.assistant}`);
const pathIds = (p) => (p.noAccess
  ? [`u:${p.user.id}`, `a:${p.assistant}`]
  : [`u:${p.user.id}`, `a:${p.assistant}`, setId(p), `d:${p.db.id}`]);

/** Places items (with an ideal centre) top to bottom without overlaps. */
function stack(items, gap) {
  let bottom = -Infinity;
  return [...items].sort((a, b) => a.ideal - b.ideal).map((it) => {
    const y = Math.max(it.ideal - it.h / 2, bottom + gap, TOP);
    bottom = y + it.h;
    return { ...it, y };
  });
}

function layout(model) {
  const nodes = [];
  const groups = [];
  const centre = {};
  let y = TOP;

  const local = new Map();
  const remoteGroups = new Map();          // instance key -> { label, users }
  model.paths.forEach((p) => {
    let bucket = local;
    if (p.remote) {
      if (!remoteGroups.has(p.remote.key)) remoteGroups.set(p.remote.key, { label: p.place, users: new Map() });
      bucket = remoteGroups.get(p.remote.key).users;
    }
    const e = bucket.get(p.user.id) || { user: p.user, inactive: p.inactive, keys: [] };
    e.keys.push(`${ASSISTANT_ORDER.indexOf(p.assistant)}:${p.set?.name || ''}`);
    bucket.set(p.user.id, e);
  });
  const sortKey = (e) => [...e.keys].sort().join('|');
  const sorted = (m) => [...m.values()].sort((a, b) => naturalCompare(sortKey(a), sortKey(b))
    || naturalCompare(a.user.username, b.user.username));
  const dataPeople = sorted(local);
  const routedPeople = [...model.routed].sort((a, b) => naturalCompare(a.instance.label, b.instance.label)
    || naturalCompare(a.user.username, b.user.username));
  const nonePeople = [...model.none].sort((a, b) => naturalCompare(a.user.username, b.user.username));

  const [pw, ph] = SIZE.person;
  const place = (key, label, list) => {
    if (!list.length) return;
    groups.push({ key, label, count: list.length, y });
    y += LABEL_H;
    list.forEach((item) => {
      const id = `u:${item.user.id}`;
      nodes.push({ id, type: 'person', x: COL_X.person, y, w: pw, h: ph, user: item.user, inactive: item.inactive });
      centre[id] = y + ph / 2;
      y += ROW;
    });
    y += GROUP_GAP;
  };
  place('data', 'Use client data here', dataPeople);
  remoteGroups.forEach((g, key) => place(`remote:${key}`, `Work on ${g.label}`, sorted(g.users)));
  place('routed', 'Work on another server', routedPeople);
  place('none', 'No client data', nonePeople);

  const column = (type, x, w, h, entries) => stack(entries.map((e) => ({ ...e, type, h })), 24).forEach((it) => {
    nodes.push({ ...it, x, w });
    centre[it.id] = it.y + it.h / 2;
  });
  const grouped = (keyOf) => {
    const m = new Map();
    model.paths.forEach((p) => {
      const key = keyOf(p);
      if (!key) return;
      const e = m.get(key) || { paths: [] };
      e.paths.push(p);
      m.set(key, e);
    });
    return m;
  };

  // 2. assistants
  column('assistant', COL_X.assistant, SIZE.assistant[0], SIZE.assistant[1],
    [...grouped((p) => `a:${p.assistant}`)].map(([id, e]) => ({
      id, assistant: e.paths[0].assistant, ideal: avg(e.paths.map((p) => centre[`u:${p.user.id}`])),
      count: new Set(e.paths.map((p) => p.user.id)).size,
    })));
  // 3. data sets
  column('set', COL_X.set, SIZE.set[0], SIZE.set[1],
    [...grouped((p) => (p.noAccess ? null : setId(p)))].map(([id, e]) => ({
      id, path: e.paths[0], ideal: avg(e.paths.map((p) => centre[`u:${p.user.id}`])),
      count: new Set(e.paths.map((p) => p.user.id)).size,
    })));
  // 4. databases + other servers
  const dbs = [...grouped((p) => (p.noAccess ? null : `d:${p.db.id}`))].map(([id, e]) => ({
    id, db: e.paths[0].db, models: [...new Set(e.paths.map((p) => p.db.model))],
    ideal: avg([...new Set(e.paths.map((p) => setId(p)))].map((s) => centre[s])),
    count: new Set(e.paths.map((p) => p.user.id)).size,
  }));
  const servers = new Map();
  model.routed.forEach((r) => {
    const id = `i:${r.instance.key}`;
    const e = servers.get(id) || { id, instance: r.instance, ys: [] };
    e.ys.push(centre[`u:${r.user.id}`]);
    servers.set(id, e);
  });
  stack([
    ...dbs.map((d) => ({ ...d, type: 'db', h: SIZE.db[1] })),
    ...[...servers.values()].map((s) => ({ id: s.id, type: 'server', instance: s.instance, ideal: avg(s.ys), h: SIZE.db[1] })),
  ], 24).forEach((it) => {
    nodes.push({ ...it, x: COL_X.db, w: SIZE.db[0] });
    centre[it.id] = it.y + it.h / 2;
  });

  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const edges = [];
  const seen = new Set();
  const edge = (from, to, kind, extra = {}) => {
    const id = `${from}|${to}`;
    if (seen.has(id) || !byId[from] || !byId[to]) return;
    seen.add(id);
    edges.push({ id, from: byId[from], to: byId[to], kind, ...extra });
  };
  model.paths.forEach((p) => {
    const [u, a, s, d] = pathIds(p);
    edge(u, a, 'scope', { path: p });
    if (s) {
      edge(a, s, 'store');
      edge(s, d, 'store');
    }
  });
  model.routed.forEach((r) => edge(`u:${r.user.id}`, `i:${r.instance.key}`, 'routed', { inactive: r.inactive }));

  const height = Math.max(y, ...nodes.map((n) => n.y + n.h)) + PAD;
  return { nodes, edges, groups, byId, width: WIDTH, height };
}

/** Everything connected to one node: { nodes, edges } (sets of ids). */
function relatedTo(id, model) {
  if (!id) return null;
  const nodes = new Set([id]);
  const edges = new Set();
  model.paths.forEach((p) => {
    const ids = pathIds(p);
    if (!ids.includes(id)) return;
    ids.forEach((x) => nodes.add(x));
    for (let i = 0; i < ids.length - 1; i += 1) edges.add(`${ids[i]}|${ids[i + 1]}`);
  });
  model.routed.forEach((r) => {
    const ids = [`u:${r.user.id}`, `i:${r.instance.key}`];
    if (!ids.includes(id)) return;
    ids.forEach((x) => nodes.add(x));
    edges.add(`${ids[0]}|${ids[1]}`);
  });
  return { nodes, edges };
}

function edgePoint(e, t) {
  const x1 = e.from.x + e.from.w;
  const y1 = e.from.y + e.from.h / 2;
  const x2 = e.to.x;
  const y2 = e.to.y + e.to.h / 2;
  const dx = (x2 - x1) / 2;
  const u = 1 - t;
  return {
    x: u * u * u * x1 + 3 * u * u * t * (x1 + dx) + 3 * u * t * t * (x2 - dx) + t * t * t * x2,
    y: u * u * u * y1 + 3 * u * u * t * y1 + 3 * u * t * t * y2 + t * t * t * y2,
  };
}

const edgePath = (e) => {
  const x1 = e.from.x + e.from.w;
  const y1 = e.from.y + e.from.h / 2;
  const x2 = e.to.x;
  const y2 = e.to.y + e.to.h / 2;
  const dx = (x2 - x1) / 2;
  return `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`;
};

const edgeClass = (e) => {
  if (e.kind !== 'scope') return e.kind;
  const p = e.path;
  if (p.noAccess) return 'scope none';
  if (p.inactive) return 'scope off';
  return `scope${p.scope.limited ? ' part' : ''}`;
};

// ─────────────────────────────────────────────────────────
// Zoomable view — zooms, never pans
// ─────────────────────────────────────────────────────────

function ZoomView({ width, height, onBackground, children }) {
  const ref = useRef(null);
  const [view, setView] = useState({ s: 1, x: 0, y: 0, fit: 1, ready: false });
  const viewRef = useRef(view);
  viewRef.current = view;

  const clampView = useCallback((s, x, y, fit) => {
    const r = ref.current.getBoundingClientRect();
    const cw = width * s;
    const ch = height * s;
    return {
      s, fit, ready: true,
      x: cw <= r.width ? (r.width - cw) / 2 : Math.min(0, Math.max(r.width - cw, x)),
      y: ch <= r.height ? (r.height - ch) / 2 : Math.min(0, Math.max(r.height - ch, y)),
    };
  }, [width, height]);

  const fitAll = useCallback(() => {
    const r = ref.current.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const fit = Math.min((r.width - 24) / width, (r.height - 24) / height, 1.1);
    setView(clampView(fit, 0, 0, fit));
  }, [width, height, clampView]);

  useLayoutEffect(() => {
    fitAll();
    const ro = new ResizeObserver(() => fitAll());
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [fitAll]);

  const zoomAt = useCallback((factor, cx, cy) => {
    const cur = viewRef.current;
    const max = Math.max(cur.fit * 4, 2);
    const s = Math.min(max, Math.max(cur.fit, cur.s * factor));
    if (Math.abs(s - cur.s) < 1e-4) return;
    setView(clampView(s, cx - (cx - cur.x) * (s / cur.s), cy - (cy - cur.y) * (s / cur.s), cur.fit));
  }, [clampView]);

  useEffect(() => {
    const el = ref.current;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * 0.0018), e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const zoomCentre = (factor) => {
    const r = ref.current.getBoundingClientRect();
    zoomAt(factor, r.width / 2, r.height / 2);
  };
  const isBackground = (e) => !e.target.closest('.dm-node, .dm-zoom');
  const percent = Math.round((view.s / view.fit) * 100);

  return (
    <div className="dm-viewport" ref={ref}
      onClick={(e) => { if (isBackground(e)) onBackground(); }}
      onDoubleClick={(e) => {
        if (!isBackground(e)) return;
        const r = ref.current.getBoundingClientRect();
        zoomAt(1.8, e.clientX - r.left, e.clientY - r.top);
      }}>
      <div className="dm-canvas" style={{
        width, height, opacity: view.ready ? 1 : 0,
        transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})`,
      }}>
        {children}
      </div>
      <div className="dm-zoom">
        <button type="button" onClick={() => zoomCentre(1 / 1.5)} disabled={percent <= 100} title="Zoom out">
          <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
        </button>
        <span>{percent}%</span>
        <button type="button" onClick={() => zoomCentre(1.5)} title="Zoom in"><FontAwesomeIcon icon={faMagnifyingGlassPlus} /></button>
        <button type="button" onClick={fitAll} disabled={percent <= 100} title="Show everything"><FontAwesomeIcon icon={faExpand} /></button>
      </div>
      <div className="dm-zoom-hint">Scroll to zoom · double-click to zoom in</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Words for the nodes
// ─────────────────────────────────────────────────────────

const assistantIcon = (key) => (key === 'ppi' ? faChartPie : faBuildingColumns);

/** "EFD data · Dev" / "EFD data · variant survista · Dev" — what kind of data, where. */
function setSub(p, meta, place) {
  const model = modelLabel(p.set?.model || p.assistant);
  const label = p.variant && p.variant !== 'mh' && p.variant !== 'ppi' ? variantLabel(meta, p.variant) : '';
  // "survista · EFD data · variant survista" says the name twice — keep the place readable
  const variant = label && label.toLowerCase() !== (p.set?.name || '').toLowerCase() ? ` · variant ${label}` : '';
  return `${model} data${variant} · ${p.place || place}`;
}

function Node({ node, meta, place, state, onPick, onHover }) {
  const style = { left: node.x, top: node.y, width: node.w, height: node.h };
  const common = {
    type: 'button', style,
    className: `dm-node dm-${node.type}${state ? ` ${state}` : ''}${node.inactive ? ' inactive' : ''}`,
    onClick: () => onPick(node.id),
    onMouseEnter: () => onHover(node.id),
    onMouseLeave: () => onHover(null),
    onFocus: () => onHover(node.id),
    onBlur: () => onHover(null),
  };
  if (node.type === 'person') {
    const name = realName(node.user);
    return (
      <button {...common}>
        <Avatar username={node.user.username} size="sm" />
        <span className="dm-text">
          <span className="dm-title">{node.user.username}</span>
          <span className="dm-sub">{node.inactive ? 'Inactive — cannot log in' : (name || ' ')}</span>
        </span>
      </button>
    );
  }
  if (node.type === 'assistant') {
    return (
      <button {...common}>
        <span className={`dm-icon ${node.assistant}`}><FontAwesomeIcon icon={assistantIcon(node.assistant)} /></span>
        <span className="dm-text">
          <span className="dm-title">{assistantLabel(meta, node.assistant)}</span>
          <span className="dm-sub">{modelLabel(node.assistant)} data</span>
        </span>
        <span className="dm-count" title={people(node.count)}>{node.count}</span>
      </button>
    );
  }
  if (node.type === 'set') {
    const p = node.path;
    return (
      <button {...common}>
        <span className="dm-icon set"><FontAwesomeIcon icon={faFolderOpen} /></span>
        <span className="dm-text">
          <span className="dm-title">{p.set ? p.set.name : 'Server default'}</span>
          <span className="dm-sub">{setSub(p, meta, place)}</span>
        </span>
        <span className="dm-count" title={people(node.count)}>{node.count}</span>
      </button>
    );
  }
  if (node.type === 'db') {
    return (
      <button {...common}>
        <span className="dm-icon db"><FontAwesomeIcon icon={faDatabase} /></span>
        <span className="dm-text">
          <span className="dm-title">{node.models.map(modelLabel).join(' / ')} database</span>
          <span className="dm-sub">{node.db.env ? 'server configuration' : `on the ${node.db.place || place} server`}</span>
        </span>
      </button>
    );
  }
  return (
    <button {...common}>
      <span className="dm-icon server"><FontAwesomeIcon icon={faServer} /></span>
      <span className="dm-text">
        <span className="dm-title">{node.instance.label}</span>
        <span className="dm-sub">Another server</span>
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────
// Explanation panel
// ─────────────────────────────────────────────────────────

function PersonRow({ user, children, onPick }) {
  return (
    <button type="button" className="dm-row" onClick={() => onPick(`u:${user.id}`)}>
      <Avatar username={user.username} size="sm" />
      <span className="dm-row-text">
        <span className="dm-row-name">{user.username}</span>
        <span className="dm-row-sub">{children}</span>
      </span>
    </button>
  );
}

function ScopeTag({ p }) {
  const cls = p.noAccess || p.inactive ? 'off' : p.scope.limited ? 'part' : 'all';
  return <span className={`dm-tag ${cls}`} title={scopeText(p)}>{scopeText(p)}</span>;
}

function Tech({ children }) {
  return <div className="dm-tech">{children}</div>;
}

function Intro({ model, graph, place }) {
  const readers = new Set(model.paths.filter((p) => !p.noAccess).map((p) => p.user.id)).size;
  const sets = graph.nodes.filter((n) => n.type === 'set').length;
  const dbs = graph.nodes.filter((n) => n.type === 'db').length;
  return (
    <>
      <h3 className="dm-panel-title">How to read this map</h3>
      <ol className="dm-steps">
        <li><span>1</span><div><b>People</b> — everyone who logs in on {place}; people who work on another server are shown with what is set for them there.</div></li>
        <li><span>2</span><div><b>Assistant</b> — Bro EFD (bank and advisor figures) or Bro PPI (portfolios).</div></li>
        <li><span>3</span><div><b>Whose data</b> — which client&apos;s data the assistant reads for that person.</div></li>
        <li><span>4</span><div><b>Database</b> — where that data is stored.</div></li>
      </ol>
      <div className="dm-legend">
        <div><i className="dm-line all" /><span>Sees <b>everything</b> of that data</span></div>
        <div><i className="dm-line part" /><span>Sees <b>only a part</b> (e.g. one advisor)</span></div>
        <div><i className="dm-line none" /><span><b>No access</b> or <b>inactive</b></span></div>
        <div><i className="dm-line routed" /><span>Works on <b>another server</b></span></div>
      </div>
      <div className="dm-stats">
        <div><strong>{readers}</strong><span>{readers === 1 ? 'person reads' : 'people read'} client data</span></div>
        <div><strong>{sets}</strong><span>{sets === 1 ? 'client data set' : 'client data sets'}</span></div>
        <div><strong>{dbs}</strong><span>{dbs === 1 ? 'database' : 'databases'}</span></div>
      </div>
      <p className="dm-tip">Click a person, an assistant, a client or a database to see who is connected to what.</p>
    </>
  );
}

function PersonPanel({ user, model, meta, place, onPick, onOpenUser }) {
  const paths = model.paths.filter((p) => p.user.id === user.id);
  const routed = model.routed.find((r) => r.user.id === user.id);
  const inactive = user.status === 'inactive';
  const name = realName(user);
  return (
    <>
      <div className="dm-panel-head">
        <Avatar username={user.username} size="lg" />
        <div>
          <h3 className="dm-panel-title">{user.username}</h3>
          {name && <div className="dm-panel-sub">{name}</div>}
        </div>
      </div>
      {inactive && <div className="dm-note warn"><FontAwesomeIcon icon={faTriangleExclamation} /> Inactive — {user.username} cannot log in, so right now sees nothing.</div>}
      {routed && (
        <p className="dm-say">
          {user.username} works on <b>{routed.instance.label}</b>, another server. {overviewNote(routed.overview)}
        </p>
      )}
      {paths[0]?.remote && (
        <div className="dm-note">
          <FontAwesomeIcon icon={faServer} />
          <span>Logs in here, works on <b>{paths[0].place}</b> — everything below is set there.</span>
        </div>
      )}
      {!routed && paths.length === 0 && (
        <p className="dm-say">{user.username} has <b>no access to client data</b> — no assistant and no audio cards that read it.</p>
      )}
      {paths.length > 0 && (
        <ul className="dm-facts">
          {paths.map((p) => (
            <li key={p.assistant}>
              <button type="button" className="dm-fact-head" onClick={() => onPick(`a:${p.assistant}`)}>
                <span className={`dm-icon small ${p.assistant}`}><FontAwesomeIcon icon={assistantIcon(p.assistant)} /></span>
                <b>{assistantLabel(meta, p.assistant)}</b>
              </button>
              {p.noAccess ? (
                <div className="dm-scope none"><FontAwesomeIcon icon={faBan} /> No access — no data set assigned.</div>
              ) : (
                <>
                  <div className="dm-fact-line">
                    reads{' '}
                    <button type="button" className="dm-link" onClick={() => onPick(setId(p))}>{p.set ? p.set.name : 'the server default'}</button>
                    <span className="dm-muted"> ({setSub(p, meta, place)})</span>
                  </div>
                  <div className={`dm-scope ${p.scope.limited ? 'part' : 'all'}`}>
                    {p.scope.limited ? <>Only <b>{partText(p.scope)}</b></>
                      : <>Everything{p.scope.value ? ` — ${p.scope.value.toLowerCase()}` : ''}</>}
                  </div>
                  <div className="dm-fact-line">
                    stored in the{' '}
                    <button type="button" className="dm-link" onClick={() => onPick(`d:${p.db.id}`)}>{modelLabel(p.db.model)} database</button>
                    {p.origin === 'user' && <span className="dm-muted"> — chosen just for {user.username}</span>}
                  </div>
                  {p.via.length > 0 && <div className="dm-fact-line dm-muted">Through {p.via.join(', ')}</div>}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="um-btn um-btn-ghost dm-open" onClick={() => onOpenUser(user)}>
        <FontAwesomeIcon icon={faUserPen} /> Open {user.username}&apos;s settings
      </button>
    </>
  );
}

function AssistantPanel({ assistant, model, meta, place, onPick }) {
  const paths = model.paths.filter((p) => p.assistant === assistant)
    .sort((a, b) => naturalCompare(a.user.username, b.user.username));
  const anyRemote = paths.some((p) => p.remote);
  return (
    <>
      <div className="dm-panel-head">
        <span className={`dm-icon big ${assistant}`}><FontAwesomeIcon icon={assistantIcon(assistant)} /></span>
        <div>
          <h3 className="dm-panel-title">{assistantLabel(meta, assistant)}</h3>
          <div className="dm-panel-sub">Reads {modelLabel(assistant)} data only</div>
        </div>
      </div>
      <p className="dm-say"><b>{people(new Set(paths.map((p) => p.user.id)).size)}</b> use it{anyRemote ? '' : ` on ${place}`}:</p>
      <div className="dm-rows">
        {paths.map((p) => (
          <PersonRow key={p.user.id} user={p.user} onPick={onPick}>
            {!p.noAccess && (
              <span className="dm-row-what">
                {p.set ? p.set.name : 'server default'}{anyRemote ? ` (${p.place || place})` : ''}
              </span>
            )}
            <ScopeTag p={p} />
          </PersonRow>
        ))}
      </div>
    </>
  );
}

function SetPanel({ id, model, meta, place, onPick }) {
  const paths = model.paths.filter((p) => !p.noAccess && setId(p) === id)
    .sort((a, b) => naturalCompare(a.user.username, b.user.username));
  const p0 = paths[0];
  if (!p0) return null;
  const schemas = Object.values(p0.set?.schema_map || {});
  return (
    <>
      <div className="dm-panel-head">
        <span className="dm-icon big set"><FontAwesomeIcon icon={faFolderOpen} /></span>
        <div>
          <h3 className="dm-panel-title">{p0.set ? p0.set.name : 'Server default'}</h3>
          <div className="dm-panel-sub">{setSub(p0, meta, place)}</div>
        </div>
      </div>
      <p className="dm-say">
        Stored in the <button type="button" className="dm-link" onClick={() => onPick(`d:${p0.db.id}`)}>{modelLabel(p0.db.model)} database</button>{' '}
        {p0.db.env ? '(server configuration)' : `on the ${p0.db.place || place} server`}.
      </p>
      <p className="dm-say"><b>{people(new Set(paths.map((p) => p.user.id)).size)}</b> can see it:</p>
      <div className="dm-rows">
        {paths.map((p) => (
          <PersonRow key={`${p.user.id}-${p.assistant}`} user={p.user} onPick={onPick}><ScopeTag p={p} /></PersonRow>
        ))}
      </div>
      <Tech>
        Technical: {p0.db.detail}{schemas.length ? ` · reads only the schemas ${schemas.join(', ')}` : ''}
        {p0.variant ? ` · variant ${variantLabel(meta, p0.variant)}` : ''}
      </Tech>
    </>
  );
}

function DatabasePanel({ id, model, place, onPick }) {
  const paths = model.paths.filter((p) => !p.noAccess && `d:${p.db.id}` === id);
  const db = paths[0]?.db;
  if (!db) return null;
  const sets = [...new Map(paths.map((p) => [setId(p), p])).values()];
  return (
    <>
      <div className="dm-panel-head">
        <span className="dm-icon big db"><FontAwesomeIcon icon={faDatabase} /></span>
        <div>
          <h3 className="dm-panel-title">{[...new Set(paths.map((p) => modelLabel(p.db.model)))].join(' / ')} database</h3>
          <div className="dm-panel-sub">{db.env ? 'server configuration' : `on the ${db.place || place} server`}</div>
        </div>
      </div>
      <p className="dm-say">Holds the data of:</p>
      <ul className="dm-plain">
        {sets.map((p) => (
          <li key={setId(p)}>
            <button type="button" className="dm-link" onClick={() => onPick(setId(p))}>{p.set ? p.set.name : 'Server default'}</button>
            <span className="dm-muted"> — {people(new Set(paths.filter((x) => setId(x) === setId(p)).map((x) => x.user.id)).size)}</span>
          </li>
        ))}
      </ul>
      <Tech>Technical: {db.detail}</Tech>
    </>
  );
}

function ServerPanel({ instanceKey, model, onPick }) {
  const list = model.routed.filter((r) => r.instance.key === instanceKey)
    .sort((a, b) => naturalCompare(a.user.username, b.user.username));
  const inst = list[0]?.instance;
  if (!inst) return null;
  return (
    <>
      <div className="dm-panel-head">
        <span className="dm-icon big server"><FontAwesomeIcon icon={faServer} /></span>
        <div>
          <h3 className="dm-panel-title">{inst.label}</h3>
          {inst.url && <div className="dm-panel-sub">{inst.url}</div>}
        </div>
      </div>
      <p className="dm-say">
        Another server. These people log in here but are handed over to it — their assistants and data live there.
      </p>
      <div className={`dm-note${list[0].overview ? ' warn' : ''}`}>
        <FontAwesomeIcon icon={list[0].overview ? faTriangleExclamation : faServer} />
        <span>{overviewNote(list[0].overview)}</span>
      </div>
      <div className="dm-rows">
        {list.map((r) => (
          <PersonRow key={r.user.id} user={r.user} onPick={onPick}>
            {r.inactive ? <span className="dm-tag off">Inactive</span> : <span className="dm-tag routed">Works there</span>}
          </PersonRow>
        ))}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
// Modal
// ─────────────────────────────────────────────────────────

export default function DataMap({ users, onClose, onOpenUser }) {
  const { client, meta } = useConsole();
  const [sets, setSets] = useState(null);
  const [assistants, setAssistants] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [remote, setRemote] = useState({});
  const place = meta.instance?.instance_label || 'this';

  const routedKeys = useMemo(
    () => [...new Set(users.map((u) => u.ui_config?.home_instance).filter(Boolean))].join(','),
    [users],
  );
  useEffect(() => {
    let cancelled = false;
    routedKeys.split(',').filter(Boolean).forEach((key) => {
      readOverview(client, key)
        .then((v) => { if (!cancelled) setRemote((r) => ({ ...r, [key]: v })); })
        .catch((e) => { if (!cancelled) setRemote((r) => ({ ...r, [key]: { available: false, reason: 'error', error: e.message } })); });
    });
    return () => { cancelled = true; };
  }, [client, routedKeys]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([client.get('/api/admin/data-sources'), client.get('/api/admin/assistants')])
      .then(([s, a]) => { if (!cancelled) { setSets(s); setAssistants(a); } })
      .catch((e) => { if (!cancelled) { setLoadError(e.message); setSets([]); setAssistants([]); } });
    return () => { cancelled = true; };
  }, [client]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (selected) setSelected(null); else onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selected, onClose]);

  const model = useMemo(() => {
    if (!sets || !assistants) return null;
    const defaults = Object.fromEntries(assistants.map((a) => [a.key, a.binding?.key]));
    const env = Object.fromEntries(assistants.map((a) => [a.key, a.env]));
    return withRemote(accessModel(users, meta, sets, defaults, env), remote);
  }, [users, meta, sets, assistants, remote]);
  const graph = useMemo(() => (model ? layout(model) : null), [model]);
  const active = hovered || selected;
  const rel = useMemo(() => (model ? relatedTo(active, model) : null), [active, model]);
  const pick = (id) => setSelected((cur) => (cur === id ? null : id));

  const panel = () => {
    const node = selected && graph?.byId[selected];
    if (!node) return <Intro model={model} graph={graph} place={place} />;
    if (node.type === 'person') return <PersonPanel user={node.user} model={model} meta={meta} place={place} onPick={setSelected} onOpenUser={onOpenUser} />;
    if (node.type === 'assistant') return <AssistantPanel assistant={node.assistant} model={model} meta={meta} place={place} onPick={setSelected} />;
    if (node.type === 'set') return <SetPanel id={node.id} model={model} meta={meta} place={place} onPick={setSelected} />;
    if (node.type === 'db') return <DatabasePanel id={node.id} model={model} place={place} onPick={setSelected} />;
    return <ServerPanel instanceKey={node.instance.key} model={model} onPick={setSelected} />;
  };

  return (
    <div className="dm-overlay" onClick={onClose}>
      <div className="dm-modal" role="dialog" aria-modal="true" aria-label="Who sees which data" onClick={(e) => e.stopPropagation()}>
        <header className="dm-head">
          <span className="dm-head-icon"><FontAwesomeIcon icon={faDiagramProject} /></span>
          <div className="dm-head-text">
            <h2>Who sees which data</h2>
            <p>On {place} · click anything to see its connections</p>
          </div>
          <div className="dm-head-legend">
            <span><i className="dm-line all" /> everything</span>
            <span><i className="dm-line part" /> only a part</span>
            <span><i className="dm-line none" /> no access</span>
          </div>
          <button type="button" className="um-icon-btn" onClick={onClose} title="Close (Esc)"><FontAwesomeIcon icon={faXmark} /></button>
        </header>

        <div className="dm-body">
          <div className="dm-stage">
            {!graph ? (
              <div className="dm-loading"><Spinner /></div>
            ) : (
              <ZoomView width={graph.width} height={graph.height} onBackground={() => setSelected(null)}>
                {COLUMNS.map((c) => (
                  <div key={c.n} className="dm-colhead" style={{ left: c.x, width: c.w }}>
                    <span className="dm-colnum">{c.n}</span>
                    <span><b>{c.title}</b><small>{c.sub}</small></span>
                  </div>
                ))}
                {graph.groups.map((g) => (
                  <div key={g.key} className="dm-group" style={{ left: COL_X.person, top: g.y, width: SIZE.person[0] }}>
                    {g.label} <span>{g.count}</span>
                  </div>
                ))}
                <svg className={`dm-edges${rel ? ' has-active' : ''}`} width={graph.width} height={graph.height}>
                  {graph.edges.map((e) => (
                    <path key={e.id} d={edgePath(e)}
                      className={`dm-edge ${edgeClass(e)}${e.kind === 'routed' && e.inactive ? ' off' : ''}${rel?.edges.has(e.id) ? ' on' : ''}`} />
                  ))}
                </svg>
                {rel && graph.edges.filter((e) => e.kind === 'scope' && rel.edges.has(e.id)).map((e) => {
                  const at = edgePoint(e, 0.42);
                  const p = e.path;
                  const cls = p.noAccess || p.inactive ? 'off' : p.scope.limited ? 'part' : 'all';
                  return (
                    <span key={`l-${e.id}`} className={`dm-elabel ${cls}`} style={{ left: at.x, top: at.y }}>
                      {p.noAccess ? 'no access' : p.inactive ? 'inactive' : p.scope.limited ? `only ${p.scope.value || partText(p.scope)}` : 'everything'}
                    </span>
                  );
                })}
                {graph.nodes.map((n) => (
                  <Node key={n.id} node={n} meta={meta} place={place} onPick={pick} onHover={setHovered}
                    state={n.id === selected ? 'selected' : rel ? (rel.nodes.has(n.id) ? 'on' : 'dim') : ''} />
                ))}
              </ZoomView>
            )}
          </div>
          <aside className="dm-panel">
            {loadError && <div className="dm-note warn"><FontAwesomeIcon icon={faTriangleExclamation} /> Data sets could not be loaded: {loadError}</div>}
            {selected && (
              <button type="button" className="dm-back" onClick={() => setSelected(null)}>
                <FontAwesomeIcon icon={faArrowLeft} /> Overview
              </button>
            )}
            {model ? panel() : <div className="dm-loading"><Spinner /></div>}
          </aside>
        </div>
      </div>
    </div>
  );
}
